/**
 * ErrorBoundary — shared render-error containment for demo routes.
 *
 * SPDX-License-Identifier: LicenseRef-Proprietary
 * Copyright (c) 2026 Invariant Holdings LLC
 */

import React from "react";
import { GRAY, WARN } from "../design-system/index.js";
import { createStructuredLogger } from "../utils/index.js";

export interface ErrorBoundaryProps {
  readonly children: React.ReactNode;
  readonly fallbackTitle?: string;
  readonly fallbackDescription?: string;
  readonly recoveryLabel?: string;
  readonly onRecover?: () => void;
}

interface ErrorBoundaryState {
  readonly error: Error | null;
}

const logger = createStructuredLogger({ tag: "demo-ui:error-boundary" });

export class ErrorBoundary extends React.Component<ErrorBoundaryProps, ErrorBoundaryState> {
  public override state: ErrorBoundaryState = { error: null };

  public static getDerivedStateFromError(error: Error): ErrorBoundaryState {
    return { error };
  }

  public override componentDidCatch(error: Error, info: React.ErrorInfo): void {
    logger.error("Demo render error", {
      componentStack: info.componentStack,
      message: error.message,
      stack: error.stack,
    });
  }

  public override render(): React.ReactNode {
    const { error } = this.state;
    if (error === null) return this.props.children;

    return (
      <section
        role="alert"
        style={{
          background: WARN[100],
          border: `1px solid ${WARN[300]}`,
          borderRadius: 8,
          color: GRAY[900],
          margin: "1rem auto",
          maxWidth: "48rem",
          padding: "1rem",
        }}
      >
        <h2
          style={{
            color: WARN[700],
            fontSize: "1rem",
            fontWeight: 700,
            margin: 0,
          }}
        >
          {this.props.fallbackTitle ?? "This demo failed to render"}
        </h2>
        <p style={{ color: GRAY[700], fontSize: "0.875rem", margin: "0.5rem 0" }}>
          {this.props.fallbackDescription ??
            "The error was logged. Reset the route or switch demos to recover."}
        </p>
        <pre
          style={{
            background: "white",
            border: `1px solid ${WARN[300]}`,
            borderRadius: 8,
            color: WARN[700],
            fontFamily: "ui-monospace, SFMono-Regular, Menlo, monospace",
            fontSize: "0.75rem",
            margin: "0.75rem 0",
            maxHeight: 180,
            overflow: "auto",
            padding: "0.75rem",
            whiteSpace: "pre-wrap",
          }}
        >
          {error.message}
        </pre>
        <button
          type="button"
          onClick={() => {
            this.setState({ error: null });
            this.props.onRecover?.();
          }}
          style={{
            background: "white",
            border: `1px solid ${GRAY[300]}`,
            borderRadius: 6,
            color: GRAY[700],
            cursor: "pointer",
            fontSize: "0.8125rem",
            fontWeight: 600,
            padding: "0.45rem 0.7rem",
          }}
        >
          {this.props.recoveryLabel ?? "Try again"}
        </button>
      </section>
    );
  }
}
